import {multi} from "../framework/src/engine/madnes/madnes_variables";
import ShuffleAnim from "./game/ShuffleAnim";
import {arrayCircleGetIndex} from "./utils/helpers";

/**
 * @constructor
 * @returns {Shuffling}
 */
export default function Shuffling() {

    /** @type {Array} */
    this._circlePoints;
    /** @type {Object<string>} */
    this.positions;

    this._minSteps = 8;
}

/**
 * @public
 * @param {number} steps
 * @returns {undefined}
 */
Shuffling.prototype.setMinSteps = function(steps) {

    if(steps < 2) {
        steps = 2;
    }

    this._minSteps = steps;
};

/**
 * @public
 * @param {number} centerX
 * @param {number} centerY
 * @param {number} diameter
 * @returns {undefined}
 */
Shuffling.prototype.initCirclePoints = function(centerX, centerY, diameter) {
    this._circlePoints = [
        { 'x': centerX, 'y': centerY - diameter },
        { 'x': centerX + diameter * 3 / 4, 'y': centerY - diameter * 3 / 4 },
        { 'x': centerX + diameter, 'y': centerY },
        { 'x': centerX + diameter * 3 / 4, 'y': centerY + diameter * 3 / 4 },
        { 'x': centerX, 'y': centerY + diameter },
        { 'x': centerX - diameter * 3 / 4, 'y': centerY + diameter * 3 / 4 },
        { 'x': centerX - diameter, 'y': centerY },
        { 'x': centerX - diameter * 3 / 4, 'y': centerY - diameter * 3 / 4 }
    ];
};

/**
 * @param {number} pos00X - position of symbol on position x 0 (first reel from left)
 * @param {number} pos00Y - position of symbol on position y 0 (first top symbol in reel 0)
 * @param {number} xOffset - x space between symbols
 * @param {number} yOffset - y space between symbols
 * @returns {undefined}
 */
Shuffling.prototype.initPositions = function(pos00X, pos00Y, xOffset, yOffset) {
    this.positions = {
        '00': { 'out': 6, 'in': 0, 'pos': { 'x': pos00X, 'y': pos00Y } },
        '10': { 'out': 6, 'in': 0, 'pos': { 'x': pos00X + xOffset * 1, 'y': pos00Y } },
        '20': { 'out': 7, 'in': 1, 'pos': { 'x': pos00X + xOffset * 2, 'y': pos00Y } },
        '30': { 'out': 0, 'in': 2, 'pos': { 'x': pos00X + xOffset * 3, 'y': pos00Y } },
        '40': { 'out': 0, 'in': 2, 'pos': { 'x': pos00X + xOffset * 4, 'y': pos00Y } },

        '01': { 'out': 5, 'in': 7, 'pos': { 'x': pos00X, 'y': pos00Y + yOffset } },
        '11': { 'out': 5, 'in': 7, 'pos': { 'x': pos00X + xOffset * 1, 'y': pos00Y + yOffset } },
        '21': { 'out': 3, 'in': 7, 'pos': { 'x': pos00X + xOffset * 2, 'y': pos00Y + yOffset } },
        '31': { 'out': 1, 'in': 3, 'pos': { 'x': pos00X + xOffset * 3, 'y': pos00Y + yOffset } },
        '41': { 'out': 1, 'in': 3, 'pos': { 'x': pos00X + xOffset * 4, 'y': pos00Y + yOffset } },

        '02': { 'out': 4, 'in': 7, 'pos': { 'x': pos00X, 'y': pos00Y + yOffset * 2 } },
        '12': { 'out': 4, 'in': 6, 'pos': { 'x': pos00X + xOffset * 1, 'y': pos00Y + yOffset * 2 } },
        '22': { 'out': 3, 'in': 5, 'pos': { 'x': pos00X + xOffset * 2, 'y': pos00Y + yOffset * 2 } },
        '32': { 'out': 2, 'in': 4, 'pos': { 'x': pos00X + xOffset * 3, 'y': pos00Y + yOffset * 2 } },
        '42': { 'out': 1, 'in': 4, 'pos': { 'x': pos00X + xOffset * 4, 'y': pos00Y + yOffset * 2 } }
    };
};

/**
 * @private
 * @param {string} from
 * @param {string} to
 * @returns {Array}
 */
Shuffling.prototype._getPointsPath = function(from, to) {

    var path = [];
    var search = true;
    var minSteps = this._minSteps;
    var pos = this.positions[from]['out'];
    var toPos = this.positions[to]['in'];

    while(search) {
        var idx = arrayCircleGetIndex(this._circlePoints, pos);
        path.push(idx);

        --pos;
        if(--minSteps > 0) {
            continue;
        }

        if(idx === toPos) {
            search = false;
        }
    }

    return path;
};

/**
 * @param {string} from
 * @param {string} to
 * @returns {Object}
 */
Shuffling.prototype.getPath = function(from, to) {

    var path = this._getPointsPath(from, to);

    var x = [];
    var y = [];

    for(var i = 0; i < path.length; i++) {
        var point = path[i];
        x.push(this._circlePoints[point]['x']);
        y.push(this._circlePoints[point]['y']);
    }

    x.push(this.positions[to]["pos"]["x"]);
    y.push(this.positions[to]["pos"]["y"]);

    return {
        'x': x,
        'y': y
    };
};

/**
 * @public
 * @returns {undefined}
 */
Shuffling.prototype.drawCircle = function() {
    this._point(this._circlePoints[0]['x'], this._circlePoints[0]['y'], 5);
    this._point(this._circlePoints[1]['x'], this._circlePoints[1]['y'], 5);
    this._point(this._circlePoints[2]['x'], this._circlePoints[2]['y'], 5);
    this._point(this._circlePoints[3]['x'], this._circlePoints[3]['y'], 5);
    this._point(this._circlePoints[4]['x'], this._circlePoints[4]['y'], 5);
    this._point(this._circlePoints[5]['x'], this._circlePoints[5]['y'], 5);
    this._point(this._circlePoints[6]['x'], this._circlePoints[6]['y'], 5);
    this._point(this._circlePoints[7]['x'], this._circlePoints[7]['y'], 5);
};

/**
 * @private
 * @param {number} x X position
 * @param {number} y Y position
 * @param {number} d Diameter
 * @returns {undefined}
 */
Shuffling.prototype._point = function(x, y, d) {
    var graphics = main_phaser.add.graphics(0, 0);
    graphics.lineStyle(1, Phaser.Color.getColor(255, 255, 255), 1);
    graphics.drawCircle(x / multi , y / multi , d / multi );
};

/**
 * @public
 * @param {MadnesObject} symbol
 * @returns {ShuffleAnim}
 */
Shuffling.prototype.createShuffle = function(symbol) {
    return new ShuffleAnim(symbol);
};